/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import {
  Box,
  Typography,
  Button,
  Container,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as StudioIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { YouTubeVideoButton } from '../../common';

/**
 * NoStudiosView component for users who don't belong to any studios yet.
 * Provides onboarding and a single "Add Studio" action that leads to browsing existing studios first.
 */
const NoStudiosView: React.FC = () => {
  const navigate = useNavigate();

  const handleAddStudio = () => {
    navigate('/studios/add');
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  const handleGettingStarted = () => {
    navigate('/getting-started');
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 4,
          py: 4,
        }}
      >
        {/* Welcome Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <StudioIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome to tools.dance
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Get started by joining a studio or creating your own
          </Typography>
        </Box>

        {/* Onboarding Info */}
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{ width: '100%', maxWidth: 600 }}
        >
          <Typography variant="body2">
            <strong>Studios</strong> are collaborative spaces that bring
            together owners, administrators, and teachers. Each studio kept
            private and secure. You can create a studio for your organization,
            customize its branding, and invite your team to join—or request to
            join an existing studio.
          </Typography>
        </Alert>

        {/* Single Add Studio Action */}
        <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Browse existing studios to join or create your own. We&apos;ll show
            you available options first to help you find the right community.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddStudio}
            sx={{
              minWidth: 200,
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
            }}
          >
            Add Studio
          </Button>

          {/* Tutorial Video */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              New to tools.dance? Watch our tutorial to learn how to create your
              first studio.
            </Typography>
            <YouTubeVideoButton
              videoId="Ke3mmXrMpo0"
              videoTitle="How to Create a New Studio on tools.dance"
              buttonText="Watch Tutorial"
              variant="outlined"
              size="small"
              fullWidth
            />
          </Box>
        </Box>

        <Divider sx={{ width: '100%', maxWidth: 600, my: 2 }} />

        {/* Additional Help */}
        <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
          <Typography variant="body2" color="text.secondary">
            Need help getting started? Check out our{' '}
            <Button
              variant="text"
              size="small"
              sx={{ p: 0, minWidth: 'auto' }}
              onClick={handleGettingStarted}
            >
              getting started guide
            </Button>{' '}
            or{' '}
            <Button
              variant="text"
              size="small"
              sx={{ p: 0, minWidth: 'auto' }}
              onClick={handleContactSupport}
            >
              contact support
            </Button>
            .
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default NoStudiosView;
